// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { X, Check, Zap, Download } from 'lucide-react';
import { api } from '../../utils/api.js';
import { capture } from '../../utils/analytics.js';

const PLANS = {
  monthly: [
    { key: 'starter', name: 'Starter', price: 49, catalogs: '5 catalogs/mo', pages: '500 pages max', highlight: false },
    { key: 'growth', name: 'Growth', price: 99, catalogs: '15 catalogs/mo', pages: '500 pages max', highlight: true },
    { key: 'pro', name: 'Pro', price: 199, catalogs: '50 catalogs/mo', pages: '1,500 pages max', highlight: false },
  ],
  annual: [
    { key: 'starter-annual', name: 'Starter', price: 33, annualTotal: 390, catalogs: '5 catalogs/mo', pages: '500 pages max', highlight: false, saving: 33 },
    { key: 'growth-annual', name: 'Growth', price: 82, annualTotal: 990, catalogs: '15 catalogs/mo', pages: '500 pages max', highlight: true, saving: 17 },
    { key: 'pro-annual', name: 'Pro', price: 166, annualTotal: 1990, catalogs: '50 catalogs/mo', pages: '1,500 pages max', highlight: false, saving: 17 },
  ],
};

function BillingToggle({ billing, setBilling }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24 }}>
      <div style={{ display: 'flex', background: 'var(--color-surface-2, #f9fafb)', borderRadius: 999, padding: 4 }}>
        {['monthly', 'annual'].map(b => (
          <button
            key={b}
            onClick={() => setBilling(b)}
            style={{
              padding: '6px 20px', borderRadius: 999, border: 'none',
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
              background: billing === b ? 'var(--color-primary, #4f46e5)' : 'transparent',
              color: billing === b ? '#fff' : 'var(--color-text-2, #6b7280)',
              transition: 'all 0.15s',
              display: 'flex', alignItems: 'center', gap: 0,
            }}
          >
            {b === 'monthly' ? 'Monthly' : (
              <>
                Annual
                <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, background: 'var(--color-green-bg)', color: 'var(--color-green-text)', borderRadius: 999, padding: '1px 7px' }}>
                  Save up to 33%
                </span>
              </>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function ExportPaywallModal({ onClose, productCount, errorCount, catalogId, trigger }) {
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [err, setErr] = useState('');
  const [billing, setBilling] = useState('monthly');
  const activePlans = PLANS[billing];

  useEffect(() => { capture('paywall_exposure', { trigger: trigger || 'export' }); }, []);

  async function handleUpgrade(planKey) {
    const billingPeriod = planKey.includes('annual') ? 'annual' : 'monthly';
    capture('paywall_conversion', { plan: planKey, billing_period: billingPeriod });
    setErr('');
    setLoadingPlan(planKey);
    try {
      const cancelPath = window.location.pathname + (window.location.search || '');
      const { url } = await api.createCheckoutSession(planKey, cancelPath);
      window.location.href = url;
    } catch (e) {
      setErr(e.message || 'Could not start checkout. Please try again.');
      setLoadingPlan(null);
    }
  }

  return (
    <div style={styles.overlay} onClick={e => e.target === e.currentTarget && onClose?.()}>
      <div style={styles.modal}>
        <button onClick={onClose} style={styles.closeBtn} aria-label="Close">
          <X size={18} />
        </button>

        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={styles.iconWrap}>
            <Download size={22} color="#4f46e5" />
          </div>
          <h2 style={styles.title}>Your catalog is ready to export</h2>
          <p style={styles.desc}>
            {productCount > 0
              ? `${productCount} product${productCount !== 1 ? 's' : ''} extracted${errorCount > 0 ? `, ${errorCount} product${errorCount !== 1 ? 's' : ''} need review` : ', ready to export'}.`
              : 'Subscribe to download your Shopify-ready CSV.'}
          </p>
        </div>

        {err && <div style={styles.errBox}>{err}</div>}

        <BillingToggle billing={billing} setBilling={setBilling} />

        <div style={styles.plans}>
          {activePlans.map(plan => (
            <div
              key={plan.key}
              style={{ ...styles.plan, ...(plan.highlight ? styles.planHighlight : {}) }}
            >
              {plan.highlight && (
                <div style={styles.badge}>
                  <Zap size={10} style={{ marginRight: 3 }} />Most Popular
                </div>
              )}
              <div style={styles.planName}>{plan.name}</div>
              <div style={styles.planPrice}>
                ${plan.price}<span style={styles.planPeriod}>/mo</span>
              </div>
              {plan.annualTotal && (
                <div style={{ fontSize: 12, color: 'var(--color-text-3, #9ca3af)', marginTop: -8, marginBottom: 10 }}>
                  billed ${plan.annualTotal}/yr
                </div>
              )}
              <div style={styles.features}>
                <Feature text={plan.catalogs} />
                <Feature text={plan.pages} />
                <Feature text="Shopify CSV export" />
                <Feature text="Full review table" />
              </div>
              <button
                onClick={() => handleUpgrade(plan.key)}
                disabled={loadingPlan !== null}
                style={{
                  ...styles.planBtn,
                  ...(plan.highlight ? styles.planBtnPrimary : {}),
                  ...(loadingPlan !== null ? { opacity: 0.7, cursor: 'not-allowed' } : {}),
                }}
              >
                {loadingPlan === plan.key ? 'Redirecting…' : `Start with ${plan.name}`}
              </button>
            </div>
          ))}
        </div>

        <p style={styles.fine}>
          Upgrade, process your catalog, and downgrade anytime.
        </p>
      </div>
    </div>
  );
}

function Feature({ text }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--color-text-2)', marginBottom: 4 }}>
      <Check size={12} color="var(--color-green)" style={{ flexShrink: 0 }} />
      {text}
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.55)',
    backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: 24,
  },
  modal: {
    background: 'var(--color-surface, #fff)',
    borderRadius: 18,
    padding: '36px 32px 28px',
    maxWidth: 680, width: '100%',
    position: 'relative',
    boxShadow: '0 24px 64px rgba(0,0,0,0.2)',
  },
  closeBtn: {
    position: 'absolute', top: 16, right: 16,
    background: 'none', border: 'none',
    color: 'var(--color-text-3, #9ca3af)',
    cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center',
  },
  iconWrap: {
    width: 52, height: 52, borderRadius: '50%',
    background: 'linear-gradient(135deg, #ede9fe, #dbeafe)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: '0 auto 14px',
  },
  title: {
    fontSize: 22, fontWeight: 800,
    color: 'var(--color-text, #111)',
    margin: '0 0 8px',
  },
  desc: {
    fontSize: 14, color: 'var(--color-text-2, #6b7280)',
    margin: 0, lineHeight: 1.5,
  },
  errBox: {
    background: 'var(--color-red-bg)', border: '1px solid var(--color-red-border)',
    borderRadius: 8, padding: '10px 14px',
    fontSize: 13, color: 'var(--color-red-text)',
    marginBottom: 16,
  },
  plans: {
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12,
    marginBottom: 16,
  },
  plan: {
    border: '1px solid var(--color-border, #e5e7eb)',
    borderRadius: 12, padding: '18px 14px',
    position: 'relative', textAlign: 'center',
  },
  planHighlight: {
    border: '2px solid #4f46e5',
    background: '#f5f3ff',
  },
  badge: {
    position: 'absolute', top: -11, left: '50%',
    transform: 'translateX(-50%)',
    background: '#4f46e5', color: '#fff',
    fontSize: 10, fontWeight: 700,
    padding: '3px 10px', borderRadius: 999,
    display: 'flex', alignItems: 'center', whiteSpace: 'nowrap',
  },
  planName: {
    fontSize: 13, fontWeight: 700,
    color: 'var(--color-text, #111)',
    marginBottom: 4,
  },
  planPrice: {
    fontSize: 26, fontWeight: 800,
    color: '#4f46e5', marginBottom: 12,
  },
  planPeriod: {
    fontSize: 13, fontWeight: 400, color: 'var(--color-text-2, #6b7280)',
  },
  features: { textAlign: 'left', marginBottom: 14 },
  planBtn: {
    width: '100%', padding: '8px 0',
    border: '1px solid var(--color-border, #d1d5db)',
    borderRadius: 8, cursor: 'pointer',
    fontSize: 13, fontWeight: 600,
    background: 'var(--color-surface, #fff)',
    color: 'var(--color-text, #374151)',
    transition: 'opacity 0.15s',
  },
  planBtnPrimary: {
    background: '#4f46e5', color: '#fff', border: 'none',
  },
  fine: {
    textAlign: 'center', fontSize: 11,
    color: 'var(--color-text-3, #9ca3af)',
    margin: 0,
  },
};
